/**
 * ConversationPersona page — /brand/conversations/:personaId
 *
 * The Persona view of §4.5.3, laid out after docs/mocks/conversations/conversation.html
 * (`renderPersona`), which the mock README declares to be the spec.
 *
 * Panel order (the mock's):
 *   1. « Résultats du persona » — presence headline, 4 KPIs, D53 category strip, side notes
 *   2. « Évolution » — 3-day rolling mean + per-configuration lines, click a day
 *   3. « Runs du <jour> » — day navigator + zone × moteur grid of run squares
 *   4. « Trois lectures de la période » — one panel, three lenses behind a segmented
 *      control (Concurrence / Sources / Cibles), scoped to the selected window.
 *      Folds the mock's blocks 4, 6 and 7 after 03-atelier-analyste.html.
 *   5. « Présence par intention observée »
 *   8. « URL les plus citées » (tier 2, on open)
 * Plus the pre-existing read-only « Configuration » card, moved into a header-button popup.
 *
 * Loading contract (§4.3): the ROLLUP row is the first and only tier-0 read; the three
 * windows re-slice `rollup.windows[win]` IN MEMORY. The lens panel is the one exception:
 * identity detail (domains, brands, cibles) exists at day grain only, so switching window
 * re-reads `…/extras?window=` (§2.4). A missing row is a 404 — and the three
 * reasons it can be missing read differently, so they never collapse into one message:
 *   - a launch is RUNNING      → per-cell turn progress (the progress poll)
 *   - turns over, FINALIZING   → consolidation in progress
 *   - no launch ever finished  → « after the first complete launch »
 * The matrix (tier 1) does NOT depend on the roll-up: it renders from the launch's own
 * RUN# rows, which is why panel 3 can be readable while panels 1/2/4-8 are not.
 *
 * Deviations from the mock, deliberate (reported with the work):
 *   - light MUI palette, MUI controls; no zone/engine scope filter in the page chrome
 *   - « Indicateur : Présence / Gagné » is absent from panel 2 — `trend` carries only
 *     `presence` per day, a « Gagné » series would be invented
 *   - the Concurrence lens drops the Présence / Gagné columns: only `mentionCounts` exists
 *     per brand (turns mentioned), not per-competitor presence or win rates
 *   - the « critère au moment du décrochage » card stays omitted (mock README §6.9)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Skeleton,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../services/api';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import {
  CONVERSATION_INTENTS,
  CONVERSATION_WINDOWS,
  TARGET_CATEGORIES,
  launchTs,
  type ConversationPersona,
  type ConversationPersonaRollup,
  type ConversationStage,
  type ConversationWindow,
  type ConversationIntentId,
  type TargetCategory,
  type TargetsManifest,
} from '@traaker/shared';
import { PersonaEditorModal } from '../components/conversations/PersonaEditorModal';
import { OutcomeDistributionChart } from '../components/conversations/OutcomeDistributionChart';
import { PersonaTrendChart } from '../components/conversations/PersonaTrendChart';
import { PersonaDayMatrix, TERMINAL_STATUSES } from '../components/conversations/PersonaDayMatrix';
import { PersonaCellUrls } from '../components/conversations/PersonaCellUrls';
import { SQUARE_STYLE } from '../components/conversations/RunSquare';
import { hasLaunchToday } from '../components/conversations/supersede';
import {
  dayKey,
  engineDisplay,
  formatDayLong,
  formatTime,
  pct,
} from '../components/conversations/personaFormat';
import { CATEGORY_CHIP_COLOR } from '../utils/targetColors';
import DomainFavicon from '../components/competitors/DomainFavicon';
import { catCount } from './targets/CATEGORIES';

// ---------------------------------------------------------------------------
// Small shared pieces — same idiom as the run view
// ---------------------------------------------------------------------------

function Panel({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, mb: 2, bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(23,21,29,.05), 0 4px 16px rgba(23,21,29,.05)', ...sx }}>
      {children}
    </Box>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={700}
      sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6875rem', display: 'block' }}
    >
      {children}
    </Typography>
  );
}

function PanelHead({ title, hint, children }: { title: string; hint?: string; children?: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1.25, mb: 1.5 }}>
      <Typography fontWeight={700} fontSize="1.0625rem">{title}</Typography>
      {children}
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Box>
  );
}

function Insight({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.75 }}>{children}</Typography>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
      {children}
    </Typography>
  );
}

/** The mock's `<details>` AND the lazy-read boundary: children mount on open only. */
function LazyDetails({ summary, children }: { summary: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        sx={{
          background: 'none', border: 'none', p: 0, cursor: 'pointer',
          font: 'inherit', fontSize: '0.8125rem', fontWeight: 600, color: 'primary.main',
        }}
      >
        {open ? '▾ ' : '▸ '}{summary}
      </Box>
      {open && <Box sx={{ mt: 1.5 }}>{children}</Box>}
    </Box>
  );
}

const CELL_SX = { borderBottom: '1px solid', borderColor: 'divider', px: 1, py: 0.75, fontSize: '0.8125rem', textAlign: 'left' as const };
const HEAD_SX = { ...CELL_SX, fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.06em', color: 'text.secondary', whiteSpace: 'nowrap' as const };

const INTENT_COLORS: Record<ConversationIntentId, string> = {
  learn: '#6d28d9',
  discover: '#1d7f89',
  compare: '#3d64c8',
};


/**
 * The three lenses of 03-atelier-analyste.html's rang 2 — a segmented control, not tabs:
 * one grid, three readings of the same scope.
 */
const LENSES = ['brands', 'sources', 'targets'] as const;
type Lens = typeof LENSES[number];

/**
 * The violet ordinal ramp, 3 steps (01's measured set: monotone lightness, adjacent gaps
 * ≥ 0.06, light end 2.72:1 on white, 1° hue spread). Beyond rank 3 the digit is printed on
 * the neutral chip, exactly like every other brand — never a 4th step.
 *
 * It is worn ONLY by our own row. 03's `.rk` is neutral for every other entity and violet
 * only on `tr.us`, because violet names one thing on this page: « nous ».
 */
const OWN_RANK_RAMP = ['#4C1D95', '#7C3AED', '#A78BFA'] as const;

/** 03's segmented control: a track in the neutral wash, the active segment lifted to paper. */
const SEG_SX = {
  bgcolor: 'action.hover',
  borderRadius: 1.5,
  p: '3px',
  gap: '3px',
  '& .MuiToggleButton-root': {
    border: 'none',
    borderRadius: '8px !important',
    px: 2,
    py: 0.75,
    fontSize: '0.78125rem',
    fontWeight: 600,
    textTransform: 'none',
    color: 'text.secondary',
    lineHeight: 1.2,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    bgcolor: 'background.paper',
    color: 'primary.dark',
    boxShadow: '0 1px 2px rgba(0,0,0,.06)',
    '&:hover': { bgcolor: 'background.paper' },
  },
};

/**
 * The three lens bodies are all mounted in the SAME grid cell and the inactive ones are
 * hidden, so the panel's height is the browser-computed max of the three and switching lens
 * cannot move a single pixel below it. 03 solved the same problem by hard-coding its measured
 * median (`min-height: 724px`), which only holds for 03's own fixture: here Cibles can be 30
 * rows and Concurrence 2, so a constant would either leave a void or still jump.
 *
 * `visibility: hidden` (not `display: none`) is what keeps the cell contributing its height;
 * it also removes the subtree from the a11y tree and from tab order on its own, and
 * `aria-hidden` is set alongside for the screen readers that honour it eagerly.
 */
const lensSlot = (active: boolean) => ({
  gridArea: '1 / 1',
  visibility: (active ? 'visible' : 'hidden') as 'visible' | 'hidden',
  pointerEvents: (active ? 'auto' : 'none') as 'auto' | 'none',
});

/**
 * GET .../personas/:personaId/extras?window= — the window-scoped counterpart of
 * `rollup.lastLaunch`, folded from the `PXDAY#` rows (§2.4). Entry shapes are typed off
 * `lastLaunch` so the two can never drift apart; the page renders them with one code path.
 *
 * Never 404s: an empty window is a zero-filled fold, which is why `launches` (not an HTTP
 * status) is what decides between the period and the `lastLaunch` fallback.
 */
interface PersonaWindowExtras {
  window: ConversationWindow;
  /** Distinct UTC days that had a row. */
  days: number;
  /** Rows summed. */
  launches: number;
  topDomains: ConversationPersonaRollup['lastLaunch']['topDomains'];
  topTargets: ConversationPersonaRollup['lastLaunch']['topTargets'];
  mentionCounts: Record<string, number>;
  stageTurns: Record<ConversationStage, number>;
  residualDomains: { count: number; cit: number };
  residualTargets: { count: number; cit: number };
  residualBrands: { count: number; turns: number };
  /** At least one day hid entries from the fold, so every count here is a LOWER BOUND. */
  isFloor: boolean;
}

/** GET .../launches/:launchId/progress */
interface LaunchProgress {
  launchId: string;
  /** Every cell terminal AND finalized (stats written) — results are readable. */
  done: boolean;
  /** Turns are over but the finalizer has not written the summaries yet. */
  finalizing: boolean;
  totalTurns: number;
  doneTurns: number;
  cells: Array<{ cellKey: string; engine: string; status: string; runs: number; maxTurns: number; completedRuns: number; turnsDone: number; finalized: boolean }>;
}

function Bar({ value, own = false, width = 90 }: { value: number; own?: boolean; width?: number | string }) {
  return (
    <Box sx={{ width, height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
      <Box sx={{ width: `${Math.min(1, Math.max(0, value)) * 100}%`, height: '100%', bgcolor: own ? 'primary.main' : 'text.disabled' }} />
    </Box>
  );
}

/** 03's `.fav` letter square — identity for a brand that has no resolvable domain. */
function LetterMark({ label, own = false }: { label: string; own?: boolean }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 17, height: 17, borderRadius: '4px', flexShrink: 0,
        display: 'grid', placeItems: 'center',
        fontSize: '0.59375rem', fontWeight: 700, color: '#fff',
        bgcolor: own ? 'primary.main' : 'text.secondary',
      }}
    >
      {(label[0] ?? '?').toUpperCase()}
    </Box>
  );
}

/** 03's `.rk`: neutral for every entity, the violet ramp only on our own row. */
function RankChip({ rank, own = false }: { rank: number; own?: boolean }) {
  const ramp = own ? OWN_RANK_RAMP[rank - 1] : undefined;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-grid', placeItems: 'center',
        width: 25, height: 25, borderRadius: '7px',
        fontSize: '0.75rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
        bgcolor: ramp ?? 'action.hover',
        color: ramp ? '#fff' : 'text.primary',
      }}
    >
      {rank}
    </Box>
  );
}

/** 03's `.tagx` — a printed label, because colour alone never carries a role. */
function RoleTag({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
        color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: '5px',
        px: 0.625, py: '1px', whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ConversationPersonaPage() {
  const { t, i18n } = useTranslation();
  const { personaId } = useParams<{ personaId: string }>();
  const { activeBrand: brand } = useBrand();
  const { tenant } = useAuth();
  const navigate = useNavigate();

  const [persona, setPersona] = useState<ConversationPersona | null>(null);
  const [rollup, setRollup] = useState<ConversationPersonaRollup | null>(null);
  const [rollupState, setRollupState] = useState<'loading' | 'computing' | 'ready' | 'error'>('loading');
  const [loading, setLoading] = useState(true);
  const [win, setWin] = useState<ConversationWindow>('7d');
  const [day, setDay] = useState<string | null>(null);
  const [manifest, setManifest] = useState<TargetsManifest | null>(null);
  const [extras, setExtras] = useState<PersonaWindowExtras | null>(null);
  const [extrasState, setExtrasState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [lens, setLens] = useState<Lens>('brands');
  const [editorOpen, setEditorOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Active launch tracking — set when runNow fires (or the persona row says
  // `lastLaunchStatus: 'running'`), cleared when the progress endpoint says done.
  const [activeLaunch, setActiveLaunch] = useState<{ launchId: string; cellCount: number } | null>(null);
  const [progress, setProgress] = useState<LaunchProgress | null>(null);
  const [brandsExpanded, setBrandsExpanded] = useState(false);
  const [domainsExpanded, setDomainsExpanded] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const tier = tenant?.tier;
  const canRunNow = tier === 'enterprise' || tier === 'agency';

  // `silent` — refresh persona + rollup in place with no skeleton (used when a
  // launch finishes); the matrix is re-read through `matrixRefreshKey`.
  const [matrixRefreshKey, setMatrixRefreshKey] = useState(0);
  const fetchData = useCallback(async (opts?: { silent?: boolean }) => {
    if (!brand?.brandId || !personaId) return;
    if (!opts?.silent) setLoading(true);
    try {
      const personas = await api.get<ConversationPersona[]>(`/brands/${brand.brandId}/personas`);
      const found = (personas ?? []).find((p) => p.personaId === personaId);
      if (!found) {
        navigate('/brand/conversations?tab=personas', { replace: true });
        return;
      }
      // A late rollup may still say 'running' for a launch the poll saw finish.
      setPersona(opts?.silent && found.lastLaunchStatus === 'running' ? { ...found, lastLaunchStatus: 'completed' } : found);

      // Auto-resume progress tracking if the last launch is still in flight
      if (!opts?.silent && found.lastLaunchId && !activeLaunch && found.lastLaunchStatus === 'running') {
        setActiveLaunch({ launchId: found.lastLaunchId, cellCount: found.cells.length });
      }

      // Tier 0 — the one roll-up read (§4.3). 404 = not computed yet, never a fold.
      try {
        if (!opts?.silent) setRollupState('loading');
        const r = await api.get<ConversationPersonaRollup>(`/brands/${brand.brandId}/personas/${personaId}/rollup`);
        setRollup(r);
        setRollupState('ready');
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setRollupState('computing');
        } else {
          setRollupState('error');
        }
      }
    } catch {
      if (!opts?.silent) navigate('/brand/conversations?tab=personas', { replace: true });
    } finally {
      if (!opts?.silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.brandId, personaId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // The Cibles referential — « Cibles mesurées : N » and the D53 category strip.
  // Left null on failure: no count is better than a wrong one.
  useEffect(() => {
    if (!brand?.brandId) return;
    let cancelled = false;
    api.get<TargetsManifest>(`/brands/${brand.brandId}/targets/summary`)
      .then((m) => { if (!cancelled) setManifest(m); })
      .catch(() => { /* leave null */ });
    return () => { cancelled = true; };
  }, [brand?.brandId]);

  // The lens panel's own read (§2.4) — keyed on the selected window, so the chip row that
  // re-slices the roll-up in memory also re-folds the identity detail. Re-read after a
  // launch finishes (`matrixRefreshKey`) so the panel is not one launch behind.
  useEffect(() => {
    if (!brand?.brandId || !personaId) return;
    let cancelled = false;
    setExtrasState('loading');
    api.get<PersonaWindowExtras>(`/brands/${brand.brandId}/personas/${personaId}/extras?window=${win}`)
      .then((e) => { if (!cancelled) { setExtras(e); setExtrasState('ready'); } })
      .catch(() => { if (!cancelled) { setExtras(null); setExtrasState('error'); } });
    return () => { cancelled = true; };
  }, [brand?.brandId, personaId, win, matrixRefreshKey]);

  // Polling: while activeLaunch is set, refresh the turn-level progress every 5s.
  const fetchDataRef = useRef(fetchData);
  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  useEffect(() => {
    if (!activeLaunch || !brand?.brandId || !persona) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const result = await api.get<LaunchProgress>(
          `/brands/${brand.brandId}/personas/${persona.personaId}/launches/${activeLaunch.launchId}/progress`,
        );
        if (cancelled) return;
        // Only touch state when a number moved — an idle tick must not repaint.
        setProgress((prev) => {
          const same = prev && prev.launchId === result.launchId && prev.done === result.done
            && prev.finalizing === result.finalizing
            && prev.doneTurns === result.doneTurns && prev.totalTurns === result.totalTurns
            && prev.cells.every((c, i) => result.cells[i]?.status === c.status && result.cells[i]?.turnsDone === c.turnsDone);
          return same ? prev : result;
        });
        if (result.done) {
          setActiveLaunch(null);
          setProgress(null);
          setMatrixRefreshKey((k) => k + 1);
          fetchDataRef.current({ silent: true });
        }
      } catch {
        // network hiccup — keep polling
      }
    };

    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLaunch?.launchId, brand?.brandId, persona?.personaId]);

  const handleDuplicate = async () => {
    if (!brand?.brandId || !persona) return;
    try {
      const result = await api.post<ConversationPersona>(
        `/brands/${brand.brandId}/personas/${persona.personaId}/duplicate`, {},
      );
      navigate(`/brand/conversations/${result.personaId}`);
      setToast({ message: t('conversations.toastDuplicated'), severity: 'success' });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : t('conversations.editorErrorGeneric');
      setToast({ message: msg, severity: 'error' });
    }
  };

  const handleRunNow = async () => {
    if (!brand?.brandId || !persona) return;
    // D62 — a second launch today replaces today's, without a refund.
    if (hasLaunchToday(persona.lastLaunchAt)
      && !window.confirm(t('conversations.runNowSupersedeConfirm'))) return;
    try {
      const result = await api.post<{ launchId: string; cellCount: number }>(
        `/brands/${brand.brandId}/personas/${persona.personaId}/run`, {},
      );
      setActiveLaunch({ launchId: result.launchId, cellCount: result.cellCount });
      setProgress(null);
      setPersona((prev) => (prev ? { ...prev, lastLaunchId: result.launchId, lastLaunchStatus: 'running' } : prev));
      setToast({ message: t('conversations.toastRunNow'), severity: 'success' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const resetTime = err.body?.retryAfterMs != null
          ? new Date(Date.now() + err.body.retryAfterMs).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
          : null;
        setToast({
          message: resetTime
            ? t('conversations.editorErrorRateLimitReset', { time: resetTime })
            : t('conversations.editorErrorGeneric'),
          severity: 'error',
        });
      } else {
        setToast({ message: t('conversations.editorErrorGeneric'), severity: 'error' });
      }
    }
  };

  // ---- derivations -------------------------------------------------------

  const slice = rollup?.windows?.[win];
  const stats = slice?.stats;
  const intent = persona ? CONVERSATION_INTENTS[persona.intentId] : null;
  const brandName = brand?.name ?? '';

  /** Days with at least one launch, ascending — the day navigator's domain. */
  const days = useMemo(() => {
    const byDay = new Map<string, Set<string>>();
    // `rollup.launchByDay` is the source of launch ids; `trend` only supplies days
    // (its own `launches` was removed 2026-09-28 — see the type). Keep reading
    // `p.launches` so a roll-up written before that change still resolves.
    for (const points of Object.values(rollup?.trend ?? {})) {
      for (const p of points) {
        if (!byDay.has(p.d)) byDay.set(p.d, new Set());
        for (const l of p.launches ?? []) byDay.get(p.d)!.add(l);
      }
    }
    for (const [d, launchId] of Object.entries(rollup?.launchByDay ?? {})) {
      if (!byDay.has(d)) byDay.set(d, new Set());
      byDay.get(d)!.add(launchId);
    }
    // No roll-up yet (or a roll-up written before `trend`): the last launch is all we know.
    if (byDay.size === 0 && persona?.lastLaunchId) {
      const at = persona.lastLaunchAt ? new Date(persona.lastLaunchAt).getTime() : launchTs(persona.lastLaunchId);
      byDay.set(dayKey(at), new Set([persona.lastLaunchId]));
    }
    // A row written in the window where neither `trend.launches` nor `launchByDay`
    // existed has days but no ids, so EVERY launch-scoped panel fell through to
    // « … pas encore consolidé » on an already-consolidated roll-up. The newest day
    // is the one the page opens on and the one `lastLaunchId` names, so seeding it
    // recovers those rows now; the rest heal on the next launch.
    if (persona?.lastLaunchId) {
      const newest = [...byDay.keys()].sort().pop();
      if (newest && byDay.get(newest)!.size === 0) byDay.get(newest)!.add(persona.lastLaunchId);
    }
    return [...byDay.entries()]
      .map(([d, launches]) => ({ d, launches: [...launches] }))
      .sort((a, b) => a.d.localeCompare(b.d));
  }, [rollup?.trend, rollup?.launchByDay, persona?.lastLaunchId, persona?.lastLaunchAt]);

  const dayIdx = day ? days.findIndex((x) => x.d === day) : days.length - 1;
  const currentDay = days[dayIdx] ?? days[days.length - 1] ?? null;
  /** The launch the matrix reads: the selected day's (newest id sorts first, D46). */
  const dayLaunchId = currentDay ? [...currentDay.launches].sort()[0] : null;

  const zoneLabels = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of persona?.cells ?? []) m[c.zone] = c.zoneMeta?.label ?? c.zone;
    return m;
  }, [persona?.cells]);

  /** Why the numbers are not there — the three states of §4.3, never collapsed into one. */
  const emptyReason: string = (() => {
    if (rollupState === 'error') return t('conversations.persona.emptyError');
    if (activeLaunch?.launchId && progress?.finalizing) return t('conversations.persona.emptyFinalizing');
    if (activeLaunch?.launchId) {
      return progress && progress.doneTurns > 0
        ? t('conversations.persona.emptyRunning', { done: progress.doneTurns, total: progress.totalTurns })
        : t('conversations.persona.emptyRunningNoTurns');
    }
    if (rollupState === 'loading') return t('conversations.persona.emptyLoading');
    // A launch exists, the roll-up row does not: the finalizer has not (yet) written it.
    if (persona?.lastLaunchId) return t('conversations.persona.emptyPendingConsolidation');
    return t('conversations.persona.emptyNever');
  })();

  const PanelEmpty = ({ message }: { message?: string }) => (
    <Typography variant="body2" color="text.secondary">{message ?? emptyReason}</Typography>
  );

  const inProgress = t('conversations.persona.inProgress');
  const windowLabel = t(`conversations.period${win}` as const);

  const deltaPts = (cur: number | null | undefined, prev: number | null | undefined) => {
    if (cur == null || prev == null) return null;
    const d = Math.round((cur - prev) * 100);
    return (
      <Typography component="span" variant="caption" fontWeight={700} sx={{ color: d > 0 ? 'success.main' : d < 0 ? 'error.main' : 'text.secondary' }}>
        {t('conversations.persona.deltaPts', { value: `${d > 0 ? '+' : ''}${d}`, window: windowLabel })}
      </Typography>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={160} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!persona || !intent) return null;

  const isRunning = activeLaunch?.launchId != null;
  const totalCells = activeLaunch?.cellCount ?? 0;
  const progressPct = progress && progress.totalTurns > 0
    ? Math.round((progress.doneTurns / progress.totalTurns) * 100)
    : 0;

  const n = slice?.n ?? 0;
  const outcome = slice?.outcome;
  const outcomeRates = outcome && n > 0
    ? { win: outcome.win / n, part: outcome.part / n, lose: outcome.lose / n }
    : null;
  const lastLaunch = rollup?.lastLaunch;

  // Panel 5 — per observed-intent presence. Replaces the per-turn drop histogram.
  // Keys are the four observedIntent values; absent = all turns fell into 'other'
  // (Layer B didn't run or only returned 'other').
  const OBSERVED_INTENT_ORDER = ['learn', 'discover', 'compare', 'intra_compare', 'authority', 'transact', 'troubleshoot', 'other'] as const;
  const intentPresenceRows = OBSERVED_INTENT_ORDER
    .map((key) => {
      const totalTurns = stats?.turnsByObservedIntent?.[key] ?? 0;
      const presentTurns = stats?.presentTurnsByObservedIntent?.[key] ?? 0;
      return { key, totalTurns, presentTurns, rate: totalTurns > 0 ? presentTurns / totalTurns : null };
    })
    .filter((r) => r.totalTurns > 0);  // hide intents with zero turns in this window

  // D53 — one bar per category that EXISTS in the referential. A category with no
  // cible is omitted, never shown as 0 % (mock README).
  //
  // The stored `presenceByCategory[i]` is presentTurnsByCategory[i] / sumTurns — the same
  // denominator as headline presence, which makes every bar a function of how many cibles
  // the category happens to hold rather than of engine behaviour (a category covered by 3
  // targets out of 400 can never move). Re-based here onto the turns where the brand WAS
  // present: presentTurnsByCategory[i] / sumPresentTurns, which reduces to
  // presenceByCategory[i] / presence. Reads as "when we show up, what carries us".
  // These are OVERLAPPING shares, not a breakdown: a turn matching both produit and marque
  // counts once per category but once in sumPresentTurns, so Σ ≥ 100 % (see the
  // `presentTurnsByCategory` note in shared/types/conversation.ts). Clamped at 1 so a
  // rounding artefact cannot render a bar wider than the panel.
  const catRows: { cat: TargetCategory; value: number | null; count: number }[] =
    manifest && slice
      ? TARGET_CATEGORIES.map((cat, i) => {
          const raw = slice.presenceByCategory?.[i] ?? null;
          const presence = slice.presence;
          return {
            cat,
            value:
              raw === null || presence === null || presence === 0
                ? null
                : Math.min(1, raw / presence),
            count: catCount(manifest, cat),
          };
        }).filter((r) => r.count > 0)
      : [];


  // ---- the three lenses' one data source ---------------------------------
  //
  // The period fold when it has anything, the last launch otherwise. The rows are NOT
  // retroactive (`PXDAY#` starts existing at the first launch that closes after §2.4 ships,
  // and the daily re-slice that would backfill them needs `sweepEnabled`, still false), so
  // for an existing persona the window is empty until a launch closes. An empty panel on a
  // persona that visibly has runs would read as a bug, hence the fallback — labelled, never
  // silent. `launches === 0` is the test, not a 404: the endpoint never 404s.
  const periodFold = extras && extras.launches > 0 ? extras : null;
  const lensData = periodFold
    ? { scope: 'period' as const, ...periodFold }
    : lastLaunch
      ? { scope: 'launch' as const, ...lastLaunch }
      : null;

  // `mentionCounts` (answers a brand was mentioned in) is the only per-brand signal there is.
  // Tracked = in the brand's competitor list.
  const ownKeys = new Set([brandName, ...((brand?.strategy?.aliases ?? []) as string[])]
    .filter(Boolean).map((x) => x.toLowerCase()));
  const trackedKeys = new Set((brand?.competitors ?? []).map((c) => c.toLowerCase()));
  const brandRows = Object.entries(lensData?.mentionCounts ?? {})
    .map(([name, turns]) => ({
      name,
      turns,
      own: ownKeys.has(name.toLowerCase()),
      tracked: trackedKeys.has(name.toLowerCase()),
    }))
    // 03's table is a RANKING: our row stays at the rank it earned, highlighted in place,
    // instead of being pinned to the top (which would print « 1 » next to a 4th place).
    .sort((a, b) => b.turns - a.turns)
    .map((r, i) => ({ ...r, rank: i + 1 }));
  const mentionTotal = brandRows.reduce((acc, r) => acc + r.turns, 0);

  // `offre` (TARGET_CATEGORIES[1]) is indexed but NEVER rendered, and nothing names it or
  // signals its absence. A ranked list, not a positional citation list, so dropping is safe.
  const targetRows = (lensData?.topTargets ?? []).filter((r) => r.category !== 'offre');

  /**
   * What the panel head says it is describing. Never left to inference.
   *
   * On a window change the previous fold stays on screen while the new one is in flight, so
   * the label is built from `periodFold.window` — the window the displayed numbers actually
   * came from — and the pending read is announced separately. Labelling them with `win`
   * would put the new chip's name on the old chip's numbers for the length of the request.
   */
  const scopeLabel = lensData?.scope === 'period'
    ? t('conversations.persona.lenses.scopePeriod', {
        window: t(`conversations.period${periodFold!.window}` as const),
        launches: periodFold!.launches,
        days: periodFold!.days,
      })
    : lensData?.scope === 'launch'
      ? t('conversations.persona.lastLaunchHint', {
          date: new Date(lensData.at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' }),
        })
      : null;
  const lensScopeHint = [
    scopeLabel,
    extrasState === 'loading'
      ? scopeLabel
        ? t('conversations.persona.lenses.scopeRefreshing', { window: windowLabel })
        : t('conversations.persona.lenses.scopeLoading', { window: windowLabel })
      : extrasState === 'error' ? t('conversations.persona.lenses.scopeError') : null,
  ].filter(Boolean).join(' · ');

  return (
    <Box sx={{ p: 3, bgcolor: '#f8f7fc', minHeight: '100vh', mx: -3, px: 3 }}>
      {/* Breadcrumb / back */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/brand/conversations?tab=personas')}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary">{t('conversations.pageTitle')}</Typography>
        <Typography variant="body2" color="text.secondary">›</Typography>
        <Typography variant="body2" fontWeight={600}>{persona.name}</Typography>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
            <Typography variant="h6" fontWeight={700}>{persona.name}</Typography>
            <Chip
              label={intent.name}
              size="small"
              sx={{
                fontSize: '0.6875rem', fontWeight: 600,
                bgcolor: INTENT_COLORS[persona.intentId] + '18',
                color: INTENT_COLORS[persona.intentId],
              }}
            />
          </Box>
          {persona.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 600, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
            >
              {persona.description}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {isRunning && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
              <CircularProgress size={16} thickness={4} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('conversations.progressRunning')}
              </Typography>
            </Box>
          )}
          <Tooltip title={isRunning ? t('conversations.progressRunNowBusy') : canRunNow ? '' : t('conversations.cardRunNowTooltipTier')}>
            <span>
              <Button variant="outlined" size="small" startIcon={<PlayCircleOutlineIcon />} disabled={!canRunNow || isRunning} onClick={handleRunNow}>
                {t('conversations.personaRunNowBtn')}
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} disabled={isRunning} onClick={handleDuplicate}>
            {t('conversations.personaDuplicateBtn')}
          </Button>
          <Button variant="outlined" size="small" startIcon={<SettingsOutlinedIcon />} onClick={() => setConfigOpen(true)}>
            {t('conversations.persona.configBtn')}
          </Button>
          <Tooltip title={isRunning ? t('conversations.persona.editDisabledRunning') : ''}>
            <span>
              <Button variant="contained" size="small" startIcon={<EditIcon />} disabled={isRunning} onClick={() => setEditorOpen(true)}>
                {t('conversations.personaEditBtn')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Active launch progress banner — per-cell turn counts (the RUNNING state) */}
      {isRunning && (
        <Box sx={{ mb: 3, p: 2, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, bgcolor: (th) => th.palette.primary.main + '12' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} thickness={5} />
              <Typography variant="body2" fontWeight={600}>
                {t('conversations.progressRunning')}
                {' · '}
                {progress?.finalizing
                  ? t('conversations.progressFinalizing')
                  : progress && progress.doneTurns > 0
                    ? t('conversations.progressTurns', { done: progress.doneTurns, total: progress.totalTurns })
                    : t('conversations.progressWaiting')}
              </Typography>
            </Box>
            {progress && progress.doneTurns > 0 && (
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{progressPct} %</Typography>
            )}
          </Box>
          <LinearProgress
            variant={progress && progress.doneTurns > 0 ? 'determinate' : 'indeterminate'}
            value={progressPct}
            sx={{ borderRadius: 4, height: 6 }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
            {(progress?.cells ?? Array.from({ length: totalCells }, () => null)).map((cell, i) => {
              const status = cell?.status ?? 'queued';
              const label = cell
                ? `${engineDisplay(cell.engine)} · ${cell.cellKey.split('#')[1]} · ${TERMINAL_STATUSES.has(status) ? cell.runs * cell.maxTurns : Math.min(cell.turnsDone, cell.runs * cell.maxTurns)}/${cell.runs * cell.maxTurns}`
                : `… ${i + 1}`;
              const color = status === 'completed' ? 'success'
                : status === 'partial' ? 'warning'
                : status === 'failed' ? 'error'
                : 'default';
              return (
                <Chip
                  key={cell?.cellKey ?? i}
                  label={label}
                  size="small"
                  color={color as 'success' | 'warning' | 'error' | 'default'}
                  variant={TERMINAL_STATUSES.has(status) ? 'filled' : 'outlined'}
                  icon={<CircularProgress size={10} thickness={5} sx={{ ml: '6px !important', display: TERMINAL_STATUSES.has(status) ? 'none' : undefined }} />}
                  sx={{ fontSize: '0.6875rem', height: 22 }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* ---------------- 1. Résultats du persona — gradient accent on the KPI panel ---------------- */}
      <Panel sx={{ backgroundImage: 'linear-gradient(135deg, rgba(109,40,217,0.05) 0%, transparent 55%)' }}>
        <PanelHead
          title={t('conversations.persona.title1')}
          hint={slice ? t('conversations.persona.scopeHint', { window: windowLabel, runs: n }) : undefined}
        >
          <Chip
            label={intent.name}
            size="small"
            sx={{ fontSize: '0.6875rem', fontWeight: 600, bgcolor: INTENT_COLORS[persona.intentId] + '18', color: INTENT_COLORS[persona.intentId] }}
          />
          {manifest && (
            <Link component="button" type="button" underline="hover" sx={{ fontSize: '0.8125rem', fontWeight: 600 }} onClick={() => navigate('/geo-strategy')}>
              {t('conversations.persona.ciblesMeasured', { count: manifest.targetCount })}
            </Link>
          )}
          <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
            {CONVERSATION_WINDOWS.map((w) => (
              <Chip
                key={w}
                label={t(`conversations.period${w}` as const)}
                size="small"
                variant={win === w ? 'filled' : 'outlined'}
                onClick={() => setWin(w)}
                sx={{ cursor: 'pointer', fontSize: '0.6875rem' }}
              />
            ))}
          </Box>
        </PanelHead>

        {!slice ? (
          <PanelEmpty />
        ) : n === 0 ? (
          <PanelEmpty message={t('conversations.persona.emptyWindow', { window: windowLabel })} />
        ) : (
          <>
            {slice.presence != null && slice.t1 != null && outcomeRates && (
              <Insight>
                {t('conversations.persona.insight', {
                  brand: brandName,
                  presence: pct(slice.presence),
                  t1: pct(slice.t1),
                  verb: intent.outcomes.verb,
                  won: pct(outcomeRates.win),
                })}
              </Insight>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Eyebrow>{t('conversations.persona.kpiPresence')}</Eyebrow>
                <Typography fontSize="2rem" fontWeight={700} sx={{ lineHeight: 1.1, mt: 0.25 }}>
                  {pct(slice.presence) ?? inProgress}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('conversations.persona.kpiPresenceSub', { brand: brandName })}
                  {slice.prev ? <> {'· '}{deltaPts(slice.presence, slice.prev.presence)}</> : null}
                </Typography>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Eyebrow>{t('conversations.persona.kpiT1')}</Eyebrow>
                <Typography fontSize="2rem" fontWeight={700} sx={{ lineHeight: 1.1, mt: 0.25 }}>
                  {pct(slice.t1) ?? inProgress}
                </Typography>
                {/* The Brand Perception bridge — t1 is what a single-prompt tool measures. */}
                <Typography variant="caption" color="text.secondary">{t('conversations.persona.kpiT1Sub')}</Typography>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Eyebrow>{t('conversations.persona.kpiSov')}</Eyebrow>
                <Typography fontSize="2rem" fontWeight={700} sx={{ lineHeight: 1.1, mt: 0.25 }}>
                  {pct(slice.shareOfVoice) ?? inProgress}
                </Typography>
                <Typography variant="caption" color="text.secondary">{t('conversations.persona.kpiSovSub')}</Typography>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Eyebrow>{t('conversations.persona.kpiOutcome')}</Eyebrow>
                {outcomeRates ? (
                  <>
                    <Box
                      role="img"
                      aria-label={[
                        t('conversations.persona.outcomeWin', { label: intent.outcomes.win.toLowerCase(), value: pct(outcomeRates.win) }),
                        t('conversations.persona.outcomePart', { label: intent.outcomes.part.toLowerCase(), value: pct(outcomeRates.part) }),
                        t('conversations.persona.outcomeLose', { label: intent.outcomes.lose.toLowerCase(), value: pct(outcomeRates.lose) }),
                      ].join(' · ')}
                      sx={{ display: 'flex', height: 12, borderRadius: 1, overflow: 'hidden', mt: 0.75, mb: 1 }}
                    >
                      <Box sx={{ flex: Math.max(outcomeRates.win, 0.001), bgcolor: SQUARE_STYLE.reco.bg }} />
                      <Box sx={{ flex: Math.max(outcomeRates.part, 0.001), bgcolor: SQUARE_STYLE.kept.bg }} />
                      <Box sx={{ flex: Math.max(outcomeRates.lose, 0.001), bgcolor: SQUARE_STYLE.never.bg }} />
                    </Box>
                    <Stack spacing={0.25}>
                      {([
                        ['outcomeWin', SQUARE_STYLE.reco.bg, intent.outcomes.win, outcomeRates.win],
                        ['outcomePart', SQUARE_STYLE.kept.bg, intent.outcomes.part, outcomeRates.part],
                        ['outcomeLose', SQUARE_STYLE.never.bg, intent.outcomes.lose, outcomeRates.lose],
                      ] as const).map(([key, color, label, value]) => (
                        <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                          <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color }} />
                          <Typography variant="caption" color="text.secondary">
                            {t(`conversations.persona.${key}`, { label: label.toLowerCase(), value: pct(value) })}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{inProgress}</Typography>
                )}
              </Box>
            </Box>

            {/* D53 — ce qui porte la présence, par catégorie de cible */}
            {catRows.length > 0 && (
              <Box sx={{ mt: 2.5 }}>
                <Eyebrow>{t('conversations.persona.catStripTitle')}</Eyebrow>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {t('conversations.persona.catStripHint')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 1.5, mt: 1 }}>
                  {catRows.map((r) => (
                    <Box key={r.cat}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="caption" fontWeight={700}>{t(`cibles.tab.${r.cat}`)}</Typography>
                        <Typography variant="caption" color="text.secondary">{pct(r.value) ?? inProgress}</Typography>
                      </Box>
                      <Bar value={r.value ?? 0} own width="100%" />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                        {r.count === 1
                          ? t('conversations.persona.catStripCountOne')
                          : t('conversations.persona.catStripCount', { count: r.count })}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {(stats?.cInduced ?? 0) === 0
                  ? t('conversations.persona.spontaneous')
                  : t('conversations.persona.induced', { count: stats?.cInduced ?? 0 })}
              </Typography>
            </Box>
          </>
        )}
      </Panel>

      {/* ---------------- 2. Évolution ---------------- */}
      <Panel>
        <PanelHead title={t('conversations.persona.title2')} hint={t('conversations.persona.trendHint')} />
        {rollup?.trend && Object.keys(rollup.trend).length > 0 ? (
          <PersonaTrendChart
            trend={rollup.trend}
            windowDays={win === '7d' ? 7 : win === '30d' ? 30 : 90}
            selectedDay={currentDay?.d ?? null}
            onSelectDay={setDay}
            zoneLabels={zoneLabels}
            meanLabel={t('conversations.persona.trendMean')}
          />
        ) : (
          <PanelEmpty message={rollupState === 'ready' ? t('conversations.persona.emptyTrend') : undefined} />
        )}
      </Panel>

      {/* ---------------- 3. Runs du <jour> ---------------- */}
      <Panel>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <IconButton
            size="small"
            aria-label={t('conversations.persona.dayPrev')}
            disabled={dayIdx <= 0}
            onClick={() => setDay(days[dayIdx - 1]?.d ?? null)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography fontWeight={700} fontSize="1.0625rem">
            {currentDay
              ? dayLaunchId
                ? t('conversations.persona.title3', {
                    day: formatDayLong(currentDay.d, i18n.language),
                    time: formatTime(launchTs(dayLaunchId), i18n.language),
                  })
                : t('conversations.persona.title3NoTime', { day: formatDayLong(currentDay.d, i18n.language) })
              : t('conversations.persona.title3NoDay')}
          </Typography>
          <IconButton
            size="small"
            aria-label={t('conversations.persona.dayNext')}
            disabled={dayIdx < 0 || dayIdx >= days.length - 1}
            onClick={() => setDay(days[dayIdx + 1]?.d ?? null)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" color="text.secondary">{t('conversations.matrixSquareHint')}</Typography>
        </Box>
        {brand && dayLaunchId ? (
          <PersonaDayMatrix
            persona={persona}
            brandId={brand.brandId}
            launchId={dayLaunchId}
            refreshKey={matrixRefreshKey}
            firstRunAt={persona.nextRunAt}
          />
        ) : (
          <PanelEmpty />
        )}
      </Panel>

      {/* ---------------- 4. Trois lectures de la période — one panel, three lenses ----------------
          03-atelier-analyste.html's rang 2: Concurrence / Sources / Cibles are one scope read
          three ways, so they are one panel behind a segmented control, not three stacked panels
          that each repeat their own scope line. The scope is the window chip row above — there
          is deliberately no second period control on this page. */}
      <Panel>
        {/* The scope line is passed as a child, not as `hint`: `PanelHead` prints children
            BEFORE the hint, and the segmented control has to be the last thing on the row so
            `ml: auto` can push it to the right edge. */}
        <PanelHead title={t('conversations.persona.lenses.title')}>
          <Typography variant="caption" color="text.secondary">{lensScopeHint}</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={lens}
            onChange={(_e, v: Lens | null) => { if (v) setLens(v); }}
            aria-label={t('conversations.persona.lenses.segAria')}
            sx={{ ...SEG_SX, ml: { sm: 'auto' }, alignSelf: 'center' }}
          >
            <ToggleButton value="brands">{t('conversations.persona.lenses.segBrands')}</ToggleButton>
            <ToggleButton value="sources">{t('conversations.persona.lenses.segSources')}</ToggleButton>
            <ToggleButton value="targets">{t('conversations.persona.lenses.segTargets')}</ToggleButton>
          </ToggleButtonGroup>
        </PanelHead>

        {/* The fallback is stated BEFORE the numbers, not under them: the reader must know
            which scope they are looking at before they read a single figure. Held back until
            the read resolves — « no launch has produced period detail yet » is a claim, and
            while the request is in flight it is not yet known to be true. */}
        {lensData?.scope === 'launch' && extrasState !== 'loading' && (
          <Box sx={{ mb: 1.75, px: 1.5, py: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('conversations.persona.lenses.fallbackNote', {
                window: windowLabel,
                date: new Date(lensData.at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }),
              })}
            </Typography>
          </Box>
        )}

        {/* `minHeight` is the floor shared by the lens stack and the empty/loading state, so
            the very first read cannot collapse the panel and then push the page down. It is a
            floor only: the stack above it is what equalises the three lenses. */}
        {!lensData ? (
          <Box sx={{ minHeight: 220 }}>
            <PanelEmpty message={extrasState === 'error' ? t('conversations.persona.lenses.scopeError') : undefined} />
          </Box>
        ) : (
          <Box sx={{ display: 'grid', minHeight: 220 }}>
            {/* --- Concurrence --- */}
            <Box sx={lensSlot(lens === 'brands')} aria-hidden={lens !== 'brands'}>
              <Eyebrow>{t('conversations.persona.lenses.brandsTitle')}</Eyebrow>
              <Insight>{t('conversations.persona.lenses.brandsIntro')}</Insight>
              {brandRows.length === 0 ? (
                <PanelEmpty message={t('conversations.persona.lenses.brandsEmpty')} />
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                      <Box component="tr">
                        <Box component="th" sx={{ ...HEAD_SX, width: 48 }}>{t('conversations.persona.lenses.colRank')}</Box>
                        <Box component="th" sx={HEAD_SX}>{t('conversations.persona.colBrand')}</Box>
                        <Box component="th" sx={{ ...HEAD_SX, textAlign: 'right' as const }}>{t('conversations.persona.colTurnsCited')}</Box>
                        <Box component="th" sx={HEAD_SX}>{t('conversations.persona.colSov')}</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {(brandsExpanded ? brandRows : brandRows.slice(0, 10)).map((r) => (
                        <Box
                          component="tr"
                          key={r.name}
                          sx={r.own ? { bgcolor: 'action.hover', '& td:first-of-type': { boxShadow: 'inset 3px 0 0 #7C3AED' } } : undefined}
                        >
                          <Box component="td" sx={CELL_SX}>
                            <RankChip rank={r.rank} own={r.own} />
                          </Box>
                          <Box component="td" sx={CELL_SX}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
                              <LetterMark label={r.name} own={r.own} />
                              <Typography variant="body2" fontWeight={r.own ? 700 : 500} component="span">{r.name}</Typography>
                              {r.own && <RoleTag>{t('conversations.persona.lenses.tagOwn')}</RoleTag>}
                              {!r.own && !r.tracked && <RoleTag>{t('conversations.runBrandUntrackedTag')}</RoleTag>}
                            </Box>
                          </Box>
                          <Box component="td" sx={{ ...CELL_SX, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' }}>{r.turns}</Box>
                          <Box component="td" sx={CELL_SX}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
                              <Typography variant="body2" sx={{ minWidth: 44, fontVariantNumeric: 'tabular-nums' }}>
                                {mentionTotal > 0 ? pct(r.turns / mentionTotal) : inProgress}
                              </Typography>
                              {mentionTotal > 0 && <Bar value={r.turns / mentionTotal} own={r.own} />}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
              {brandRows.length > 10 && (
                <Box sx={{ mt: 1 }}>
                  <Button size="small" variant="text" sx={{ fontSize: '0.75rem', p: 0, minWidth: 0 }} onClick={() => setBrandsExpanded((v) => !v)}>
                    {brandsExpanded ? t('conversations.persona.lenses.brandsCollapse') : t('conversations.persona.lenses.brandsExpand', { n: brandRows.length - 10 })}
                  </Button>
                </Box>
              )}
              {(lensData.residualBrands?.count ?? 0) > 0 && (
                <Note>
                  {lensData.scope === 'period'
                    ? t('conversations.persona.lenses.residualBrands', {
                        count: lensData.residualBrands.count, turns: lensData.residualBrands.turns,
                        launches: lensData.launches, days: lensData.days,
                      })
                    : t('conversations.persona.competitorsResidual', {
                        count: lensData.residualBrands.count, turns: lensData.residualBrands.turns,
                      })}
                </Note>
              )}
              <Note>{t('conversations.persona.lenses.brandsNote')}</Note>
            </Box>

            {/* --- Sources --- */}
            <Box sx={lensSlot(lens === 'sources')} aria-hidden={lens !== 'sources'}>
              <Eyebrow>{t('conversations.persona.lenses.sourcesTitle')}</Eyebrow>
              <Insight>{t('conversations.persona.lenses.sourcesIntro')}</Insight>
              {(lensData.topDomains ?? []).length === 0 ? (
                <PanelEmpty message={t('conversations.persona.lenses.sourcesEmpty')} />
              ) : (() => {
                const allDomains = [...(lensData.topDomains ?? [])].sort((a, b) => b.cit - a.cit);
                const maxCit = allDomains[0]?.cit ?? 1;
                const visibleDomains = domainsExpanded ? allDomains : allDomains.slice(0, 10);
                return (
                  <Stack spacing={0.75}>
                    {visibleDomains.map((r) => (
                      <Box key={r.domain} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <DomainFavicon domain={r.domain} name={r.domain} size={16} />
                        <Typography
                          variant="caption"
                          sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: r.ownDomain ? 700 : 400, color: r.ownDomain ? 'primary.main' : undefined }}
                        >
                          {r.domain}
                        </Typography>
                        {r.ownDomain && <RoleTag>{t('conversations.persona.lenses.tagOwnDomain')}</RoleTag>}
                        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{r.cit}</Typography>
                        <Bar value={r.cit / maxCit} own={r.ownDomain} width={70} />
                      </Box>
                    ))}
                    {allDomains.length > 10 && (
                      <Box sx={{ mt: 0.5 }}>
                        <Button size="small" variant="text" sx={{ fontSize: '0.75rem', p: 0, minWidth: 0 }} onClick={() => setDomainsExpanded((v) => !v)}>
                          {domainsExpanded ? t('conversations.persona.sourcesLessBtn') : t('conversations.persona.sourcesMoreBtn', { count: allDomains.length - 10 })}
                        </Button>
                      </Box>
                    )}
                  </Stack>
                );
              })()}
              {(lensData.residualDomains?.count ?? 0) > 0 && (
                <Note>
                  {lensData.scope === 'period'
                    ? t('conversations.persona.lenses.residualDomains', {
                        count: lensData.residualDomains.count, cit: lensData.residualDomains.cit,
                        launches: lensData.launches, days: lensData.days,
                      })
                    : t('conversations.persona.sourcesResidual', {
                        count: lensData.residualDomains.count, cit: lensData.residualDomains.cit,
                      })}
                </Note>
              )}
            </Box>

            {/* --- Cibles --- */}
            <Box sx={lensSlot(lens === 'targets')} aria-hidden={lens !== 'targets'}>
              <Eyebrow>{t('conversations.persona.lenses.targetsTitle')}</Eyebrow>
              <Insight>{t('conversations.persona.lenses.targetsIntro')}</Insight>
              {targetRows.length === 0 ? (
                <PanelEmpty message={t('conversations.persona.lenses.targetsEmpty')} />
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  {(() => {
                    // Max runs across top targets — used as 100% for the bar.
                    // When the brand target is in the list (most common case), it sets
                    // the ceiling and everything else is proportional to it.
                    const maxRuns = Math.max(...targetRows.map((r) => r.runs), 1);
                    return (
                      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                        <Box component="thead">
                          <Box component="tr">
                            <Box component="th" sx={HEAD_SX}>{t('conversations.persona.colTarget')}</Box>
                            <Box component="th" sx={HEAD_SX}>{t('conversations.persona.colCategory')}</Box>
                            <Box component="th" sx={{ ...HEAD_SX, textAlign: 'right' as const }}>{t('conversations.persona.colCitations')}</Box>
                            <Box component="th" sx={{ ...HEAD_SX, textAlign: 'right' as const }}>{t('conversations.persona.colTurns')}</Box>
                            <Box component="th" sx={HEAD_SX}>{t('conversations.persona.colRuns')}</Box>
                          </Box>
                        </Box>
                        <Box component="tbody">
                          {targetRows.map((r) => {
                            const runsPct = Math.round((r.runs / maxRuns) * 100);
                            // `marque` has no entry in CATEGORY_CHIP_COLOR, so it falls back to
                            // violet — which is the right answer: a marque target IS « nous ».
                            const dotColor = CATEGORY_CHIP_COLOR[r.category]?.fg ?? 'primary.main';
                            return (
                              <Box component="tr" key={r.targetId}>
                                {/* Cible label with colored dot */}
                                <Box component="td" sx={CELL_SX}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
                                    <Typography variant="body2" component="span" sx={{ fontWeight: r.category === 'marque' ? 600 : 400 }}>
                                      {r.value.split('|').join(' · ')}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box component="td" sx={CELL_SX}>
                                  <Chip
                                    label={t(`cibles.tab.${r.category}`)}
                                    size="small"
                                    sx={{
                                      height: 20, fontSize: '0.625rem', fontWeight: 600,
                                      bgcolor: CATEGORY_CHIP_COLOR[r.category]?.bg ?? 'action.hover',
                                      color: CATEGORY_CHIP_COLOR[r.category]?.fg ?? 'text.secondary',
                                    }}
                                  />
                                </Box>
                                <Box component="td" sx={{ ...CELL_SX, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' }}>{r.cit}</Box>
                                <Box component="td" sx={{ ...CELL_SX, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' }}>{r.turns}</Box>
                                {/* Runs: percentage + bar */}
                                <Box component="td" sx={CELL_SX}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography variant="body2" sx={{ minWidth: 36, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                                      {runsPct} %
                                    </Typography>
                                    <Box sx={{ flex: 1, minWidth: 80, height: 6, borderRadius: 3, bgcolor: 'action.hover', overflow: 'hidden' }}>
                                      <Box sx={{ width: `${runsPct}%`, height: '100%', borderRadius: 3, bgcolor: dotColor }} />
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              )}
              {(lensData.residualTargets?.count ?? 0) > 0 && (
                <Note>
                  {lensData.scope === 'period'
                    ? t('conversations.persona.lenses.residualTargets', {
                        count: lensData.residualTargets.count, cit: lensData.residualTargets.cit,
                        launches: lensData.launches, days: lensData.days,
                      })
                    : t('conversations.persona.targetsResidual', {
                        count: lensData.residualTargets.count, cit: lensData.residualTargets.cit,
                      })}
                </Note>
              )}
              <Note>{t('conversations.persona.targetsNote')}</Note>
            </Box>
          </Box>
        )}

        {/* The floor disclosure belongs to the panel, not to one lens: the same day-grain cap
            produced all three lists. Only the period fold can know it (`isFloor`). */}
        {lensData?.scope === 'period' && lensData.isFloor && (
          <Note>{t('conversations.persona.lenses.floorNote')}</Note>
        )}
      </Panel>

      {/* ---------------- 5. Présence par intention observée ---------------- */}
      <Panel>
        <PanelHead
          title={t('conversations.persona.title5')}
          hint={slice ? t('conversations.persona.scopeHint', { window: windowLabel, runs: n }) : undefined}
        />
        {!stats || n === 0 ? (
          <PanelEmpty message={slice && n === 0 ? t('conversations.persona.emptyWindow', { window: windowLabel }) : undefined} />
        ) : intentPresenceRows.length === 0 ? (
          <PanelEmpty message={t('conversations.persona.intentPresenceEmpty')} />
        ) : (
          <>
            <Insight>{t('conversations.persona.intentPresenceInsight', { brand: brandName })}</Insight>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {intentPresenceRows.map((r) => (
                <Box key={r.key}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                    <Typography variant="caption" fontWeight={600}>
                      {t(`conversations.persona.observedIntent_${r.key}`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.rate !== null ? pct(r.rate) : '–'}
                      {' · '}
                      {t('conversations.persona.intentPresenceTurns', { count: r.totalTurns })}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 6, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}>
                    <Box
                      sx={{
                        height: '100%',
                        width: r.rate !== null ? `${Math.round(r.rate * 100)}%` : '0%',
                        bgcolor: (INTENT_COLORS as Record<string, string>)[r.key] ?? '#888',
                        borderRadius: 1,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
            {slice?.byCell && slice.byCell.some((c) => c.stats) && (
              <Box sx={{ mt: 2 }}>
                <Eyebrow>{t('conversations.persona.diagDetails')}</Eyebrow>
                <Box sx={{ mt: 1 }}>
                  <OutcomeDistributionChart
                    cells={slice.byCell.filter((c) => c.stats).map((c) => ({ cellKey: c.cellKey, stats: c.stats }))}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Panel>

      {/* ---------------- 8. URL les plus citées (tier 2, read on open) ---------------- */}
      <Panel>
        <PanelHead title={t('conversations.persona.title8')} hint={t('conversations.persona.urlsHint')} />
        {brand && dayLaunchId && persona.cells.length > 0 ? (
          <LazyDetails summary={t('conversations.persona.urlsSummary')}>
            <PersonaCellUrls
              brandId={brand.brandId}
              personaId={persona.personaId}
              launchId={dayLaunchId}
              cells={persona.cells}
            />
          </LazyDetails>
        ) : (
          <PanelEmpty />
        )}
      </Panel>

      {/* ---------------- Configuration — read-only, opened from the header ---------------- */}
      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.0625rem' }}>{t('conversations.persona.configTitle')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <Box>
              <Eyebrow>{t('conversations.persona.configIntent')}</Eyebrow>
              <Typography variant="body2">{intent.name}</Typography>
            </Box>
            <Box>
              <Eyebrow>{t('conversations.persona.configCadence')}</Eyebrow>
              <Typography variant="body2">
                {persona.cadence && persona.cadence !== 'off'
                  ? persona.cadence.interval === 1
                    ? t(`conversations.cadence${persona.cadence.unit === 'days' ? 'Daily' : persona.cadence.unit === 'weeks' ? 'Weekly' : 'Monthly'}`)
                    : t(`conversations.persona.configCadenceEvery${persona.cadence.unit === 'days' ? 'Days' : persona.cadence.unit === 'weeks' ? 'Weeks' : 'Months'}`, { interval: persona.cadence.interval })
                  : t('conversations.persona.configCadenceOff')}
              </Typography>
            </Box>
            <Box>
              <Eyebrow>{t('conversations.persona.configMaxTurns')}</Eyebrow>
              <Typography variant="body2">{persona.maxTurns}</Typography>
            </Box>
            <Box>
              <Eyebrow>{t('conversations.persona.configMode')}</Eyebrow>
              <Typography variant="body2">
                {t(`conversations.persona.mode${persona.mode.charAt(0).toUpperCase()}${persona.mode.slice(1)}`)}
              </Typography>
            </Box>
            {persona.nextRunAt && (
              <Box>
                <Eyebrow>{t('conversations.persona.configNextRun')}</Eyebrow>
                <Typography variant="body2">
                  {new Date(persona.nextRunAt).toLocaleString(i18n.language, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Eyebrow>{t('conversations.personaCells')}</Eyebrow>
          {persona.cells.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('conversations.persona.configNoCells')}</Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {Object.entries(
                persona.cells.reduce<Record<string, { engine: string; runs: number }[]>>((acc, c) => {
                  const key = `${c.zoneMeta?.label ?? c.zone}|${c.language}`;
                  (acc[key] ??= []).push({ engine: c.engine, runs: c.runs });
                  return acc;
                }, {}),
              ).map(([key, entries]) => {
                const [zoneLabel, lang] = key.split('|');
                return (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={500} sx={{ minWidth: 120 }}>{zoneLabel}</Typography>
                    <Chip label={lang} size="small" variant="outlined" sx={{ fontSize: '0.6875rem', fontFamily: 'monospace' }} />
                    {entries.map((e) => (
                      <Chip key={e.engine} label={`${engineDisplay(e.engine)} × ${e.runs}`} size="small" sx={{ fontSize: '0.6875rem', fontWeight: 600 }} />
                    ))}
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {brand && (
        <PersonaEditorModal
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          persona={persona}
          brandId={brand.brandId}
          onSuccess={(updated) => {
            setPersona(updated);
            setToast({ message: t('conversations.toastUpdated'), severity: 'success' });
          }}
        />
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
